import React, { useMemo, useState } from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import StudentsTable from '../students/StudentsTable';
import EmptyState from '../common/EmptyState';
import DrilldownHeader from './DrilldownHeader';
import Segments from './Segments';
import useKeyedRequest from '../../hooks/useKeyedRequest';
import { BUCKETS, bucketMembers, itemsOf } from '../../lib/derive';
import { BUCKET_TABLES, nextSort, sortStudents } from '../../lib/studentTable';
import { studentsCsv, csvFilename } from '../../lib/studentsCsv';
import { downloadCsv } from '../../lib/downloadCsv';
import { LIFETIME_CAPTION, windowCaption } from '../../lib/format';

// The buckets whose table has "Items so far": completions since each
// account opened, from the Students list for period "all".
const NEEDS_LIFETIME_ITEMS = new Set(['inactive_30d', 'no_documents']);

const EMPTY_TEXT = {
  never_started: 'Every student here has started.',
  inactive_30d: 'Nobody here has been inactive for more than 30 days.',
  low_accuracy: 'Nobody here is below 40% over at least 10 questions.',
  no_documents: 'Every student here has a document on file.',
};

/**
 * One Needs attention bucket as a list (mockup state 1). Its rows are
 * lib/derive's bucketMembers over the tracker dashboard rows, the same
 * call the Overview counts with, so the row count is always the number on
 * the Overview. The four buckets switch in place; each has its own columns
 * and first sort (lib/studentTable BUCKET_TABLES). No checkboxes: group
 * changes belong to the Students tab.
 */
const AttentionBucket = ({
  bucket, members, lifetimeRows, lifetimeError, scopeLabel, period, onSwitch, onBack, onOpenStudent,
}) => {
  const table = BUCKET_TABLES[bucket];
  const meta = BUCKETS.find((b) => b.key === bucket);
  const [sort, setSort] = useState(table.sort);
  const wantsItems = NEEDS_LIFETIME_ITEMS.has(bucket);

  const rows = useMemo(() => {
    const list = members[bucket];
    if (!wantsItems) return list;
    const items = new Map((lifetimeRows || []).map((s) => [s.id, itemsOf(s)]));
    return list.map((s) => ({ ...s, items_so_far: items.has(s.id) ? items.get(s.id) : null }));
  }, [members, bucket, wantsItems, lifetimeRows]);

  const sorted = useMemo(() => sortStudents(rows, sort, table.columns), [rows, sort, table.columns]);
  const caption = meta.lifetime ? LIFETIME_CAPTION : windowCaption(period);

  return (
    <div className="td-students td-drill">
      <DrilldownHeader
        title="Needs attention"
        subtitle={[scopeLabel, caption].filter(Boolean).join(' · ')}
        onBack={onBack}
        exportDisabled={!sorted.length}
        onExport={() => downloadCsv(csvFilename(`${scopeLabel} ${meta.label}`), studentsCsv(sorted, table.columns))}
      />
      <Segments
        label="Needs attention bucket"
        options={BUCKETS.map((b) => ({ key: b.key, label: b.label, count: members[b.key].length }))}
        value={bucket}
        onChange={onSwitch}
      />
      {wantsItems && lifetimeError ? <div className="td-error">{lifetimeError}</div> : null}
      {sorted.length === 0 ? (
        <EmptyState title={`${meta.label}: nobody`} body={EMPTY_TEXT[bucket]} />
      ) : (
        <StudentsTable
          label={meta.label}
          students={sorted}
          columns={table.columns}
          selectable={false}
          sort={sort}
          onSort={(key) => setSort((prev) => nextSort(prev, key, table.columns))}
          onOpenStudent={onOpenStudent}
        />
      )}
    </div>
  );
};

const AttentionDrilldown = ({
  students, bucket, scope, period, roster, ...rest
}) => {
  const members = useMemo(() => bucketMembers(students), [students]);
  // Loaded here, not per bucket, so switching between the two buckets that
  // show it does not ask again. With period "all" the roster already is it.
  const lifetime = useKeyedRequest({
    key: `${scope}|all`,
    enabled: NEEDS_LIFETIME_ITEMS.has(bucket) && period !== 'all' && Boolean(scope),
    load: () => organisationsAPI.getStudents({ scope, period: 'all' }),
    errorText: 'Could not load items so far.',
  });
  const lifetimeRows = period === 'all' ? roster : lifetime.data?.students;
  // Keyed by bucket, so switching buckets starts at that bucket's own sort.
  return (
    <AttentionBucket
      key={bucket}
      bucket={bucket}
      members={members}
      lifetimeRows={lifetimeRows}
      lifetimeError={period === 'all' ? '' : lifetime.error}
      period={period}
      {...rest}
    />
  );
};

export default AttentionDrilldown;
